import React, { useContext, useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  AlertTriangle,
  ArrowUpRight,
  BadgeCheck,
  BarChart3,
  Bell,
  BriefcaseBusiness,
  Building2,
  CalendarDays,
  CheckCircle2,
  ChevronRight,
  CircleDollarSign,
  ClipboardCheck,
  Clock3,
  CreditCard,
  Download,
  FileSpreadsheet,
  Filter,
  HandCoins,
  Landmark,
  LayoutDashboard,
  LogOut,
  Menu,
  PiggyBank,
  Receipt,
  Search,
  Settings2,
  ShieldCheck,
  ShieldAlert,
  Sparkles,
  TrendingUp,
  Upload,
  UserCheck,
  UserPlus,
  Users,
  Vote,
  Wallet,
  X,
  Activity,
  Banknote,
  FileBarChart,
  Layers3,
} from 'lucide-react';
import { AuthContext } from '../context/AuthContext';

const FONT_SCALE = 1.15;
const fs = (px) => `${Math.round(px * FONT_SCALE * 100) / 100}px`;

const formatCurrency = (value) =>
  `₦${Number(value || 0).toLocaleString('en-NG', {
    maximumFractionDigits: 0,
  })}`;

const sectionIcons = {
  overview: LayoutDashboard,
  members: Users,
  loans: HandCoins,
  payroll: FileSpreadsheet,
  finance: Wallet,
  investments: BriefcaseBusiness,
  elections: Vote,
  compliance: ShieldCheck,
};

const AdminDashboard = () => {
  const { user, logout } = useContext(AuthContext);

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeSection, setActiveSection] = useState('overview');
  const [activeAction, setActiveAction] = useState('members');
  const [loanFilter, setLoanFilter] = useState('all');
  const [memberFilter, setMemberFilter] = useState('all');
  const [interestRate, setInterestRate] = useState(10);

  const [pendingMembers, setPendingMembers] = useState([
    {
      id: 'FAAN-2026-001',
      name: 'Jane Doe',
      email: 'jane.doe@example.com',
      contribution: 10000,
      department: 'Operations',
      status: 'Pending',
      joined: '12-Mar-2026',
      kyc: 'BVN Submitted',
    },
    {
      id: 'FAAN-2026-002',
      name: 'John Smith',
      email: 'john.smith@example.com',
      contribution: 20000,
      department: 'Engineering',
      status: 'Pending',
      joined: '11-Mar-2026',
      kyc: 'Staff Match',
    },
    {
      id: 'FAAN-2026-003',
      name: 'Aisha Bello',
      email: 'aisha.bello@example.com',
      contribution: 15000,
      department: 'Finance',
      status: 'KYC Review',
      joined: '10-Mar-2026',
      kyc: 'NIN Verification',
    },
    {
      id: 'FAAN-2026-004',
      name: 'Musa Aliyu',
      email: 'musa.aliyu@example.com',
      contribution: 12000,
      department: 'Security',
      status: 'Pending',
      joined: '09-Mar-2026',
      kyc: 'Awaiting Admin Check',
    },
  ]);

  const [loanRequests, setLoanRequests] = useState([
    {
      id: 'LN001',
      memberId: 'FAAN-2026-044',
      memberName: 'Ibrahim Musa',
      amount: 500000,
      months: 12,
      purpose: 'Business Expansion',
      type: 'Business',
      status: 'Committee Review',
      score: 'High',
    },
    {
      id: 'LN002',
      memberId: 'FAAN-2026-052',
      memberName: 'Chinelo Okafor',
      amount: 250000,
      months: 6,
      purpose: 'School Fees',
      type: 'Emergency',
      status: 'Admin Review',
      score: 'Medium',
    },
    {
      id: 'LN003',
      memberId: 'FAAN-2026-066',
      memberName: 'David Ojo',
      amount: 120000,
      months: 4,
      purpose: 'Medical Support',
      type: 'Emergency',
      status: 'Ready to Disburse',
      score: 'High',
    },
    {
      id: 'LN004',
      memberId: 'FAAN-2026-071',
      memberName: 'Fatima Sule',
      amount: 300000,
      months: 10,
      purpose: 'Home Support',
      type: 'Personal',
      status: 'Admin Review',
      score: 'Good',
    },
  ]);

  const [products] = useState([
    {
      title: 'Housing Estate Scheme',
      subscribers: 84,
      roi: '12% Annual',
      status: 'Open',
      units: '18 units left',
    },
    {
      title: 'Retail Supply Pool',
      subscribers: 126,
      roi: '9% Annual',
      status: 'Active',
      units: 'Rolling plan',
    },
    {
      title: 'Transport Asset Fund',
      subscribers: 41,
      roi: '14% Annual',
      status: 'Open',
      units: 'Limited slots',
    },
  ]);

  const [elections] = useState([
    {
      title: 'Executive Committee Election',
      voters: 482,
      turnout: '74%',
      status: 'Live',
    },
    {
      title: 'Dividend Reinvestment Poll',
      voters: 318,
      turnout: '49%',
      status: 'Upcoming',
    },
    {
      title: 'New Bylaw Amendment Poll',
      voters: 401,
      turnout: '61%',
      status: 'Closed',
    },
  ]);

  const [activityLog] = useState([
    {
      title: 'Payroll deduction file uploaded',
      meta: 'March cooperative deductions successfully reconciled.',
      time: '10 mins ago',
      type: 'success',
    },
    {
      title: 'Loan request flagged for committee',
      meta: 'LN001 exceeds admin approval threshold.',
      time: '32 mins ago',
      type: 'warning',
    },
    {
      title: 'New member registration submitted',
      meta: 'Aisha Bello completed BVN and staff validation.',
      time: '1 hour ago',
      type: 'info',
    },
    {
      title: 'Dividend allocation report generated',
      meta: 'FY 2025 report available for export.',
      time: 'Today',
      type: 'success',
    },
  ]);

  const [payrollUpload] = useState({
    fileName: 'FAAN_MARCH_PAYROLL.csv',
    records: 1000,
    processed: 976,
    pending: 24,
    failed: 4,
  });

  const totalMembers = 1284;
  const activeMembers = 1210;
  const monthlyCollections = 12480000;
  const pendingDisbursement = 870000;
  const totalLoanPortfolio = 28500000;
  const dividendPool = 6400000;
  const devCommission = 200000;
  const payrollCompletion = 97;
  const complianceScore = 92;
  const inactiveMembers = totalMembers - activeMembers;

  const financeCards = [
    {
      title: 'Monthly Collections',
      value: formatCurrency(monthlyCollections),
      icon: PiggyBank,
    },
    {
      title: 'Loan Portfolio',
      value: formatCurrency(totalLoanPortfolio),
      icon: CreditCard,
    },
    {
      title: 'Dividend Pool',
      value: formatCurrency(dividendPool),
      icon: CircleDollarSign,
    },
    {
      title: 'Service Commission',
      value: formatCurrency(devCommission),
      icon: Receipt,
    },
  ];

  const complianceItems = [
    'Role-based access control active',
    'Audit logs enabled for admin actions',
    '2FA and KYC enforcement configured',
    'Sensitive records protected in transit',
  ];

  const memberSegments = [
    { label: 'Active Members', value: activeMembers },
    { label: 'Inactive Members', value: inactiveMembers },
    { label: 'Pending Onboarding', value: pendingMembers.length },
  ];

  const recentReports = [
    { title: 'Contribution Summary', date: '12-Mar-2026', status: 'Ready' },
    { title: 'Dividend Board Pack', date: '11-Mar-2026', status: 'Ready' },
    { title: 'Payroll Reconciliation', date: '10-Mar-2026', status: 'Updated' },
  ];

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      const tablet = window.innerWidth < 1120;
      setIsMobile(mobile);
      setIsTablet(tablet);
      if (!tablet) setSidebarOpen(false);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const approveMember = (memberId) => {
    setPendingMembers((prev) => prev.filter((m) => m.id !== memberId));
  };

  const rejectMember = (memberId) => {
    setPendingMembers((prev) => prev.filter((m) => m.id !== memberId));
  };

  const approveLoan = (loanId) => {
    setLoanRequests((prev) => prev.filter((l) => l.id !== loanId));
  };

  const rejectLoan = (loanId) => {
    setLoanRequests((prev) => prev.filter((l) => l.id !== loanId));
  };

  const filteredMembers = useMemo(() => {
    return pendingMembers.filter((member) => {
      const matchesSearch =
        member.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        member.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        member.department.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesFilter =
        memberFilter === 'all'
          ? true
          : member.status.toLowerCase() === memberFilter.toLowerCase();

      return matchesSearch && matchesFilter;
    });
  }, [pendingMembers, searchTerm, memberFilter]);

  const filteredLoans = useMemo(() => {
    return loanRequests.filter((loan) => {
      const matchesSearch =
        loan.memberName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        loan.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        loan.type.toLowerCase().includes(searchTerm.toLowerCase());

      const normalizedStatus = loan.status.toLowerCase();
      const matchesFilter =
        loanFilter === 'all'
          ? true
          : loanFilter === 'admin'
          ? normalizedStatus.includes('admin')
          : loanFilter === 'committee'
          ? normalizedStatus.includes('committee')
          : loanFilter === 'ready'
          ? normalizedStatus.includes('ready')
          : true;

      return matchesSearch && matchesFilter;
    });
  }, [loanRequests, searchTerm, loanFilter]);

  const currentActionConfig = {
    members: {
      title: 'Member Approval Center',
      description:
        'Review new registrations, validate KYC, approve onboarding, and activate cooperative wallets.',
      accent: '#16a34a',
    },
    loans: {
      title: 'Loan Operations Desk',
      description:
        'Handle admin approvals, committee escalation, disbursement readiness, and repayment controls.',
      accent: '#0f766e',
    },
    payroll: {
      title: 'Payroll Upload & Reconciliation',
      description:
        'Upload salary deductions, reconcile member contributions, and monitor unresolved records.',
      accent: '#2563eb',
    },
    products: {
      title: 'Investment Product Manager',
      description:
        'Create and manage cooperative-backed products, shares, and subscription windows.',
      accent: '#7c3aed',
    },
    elections: {
      title: 'Election & Voting Control',
      description:
        'Launch elections, manage polls, monitor turnout, and publish secure results.',
      accent: '#db2777',
    },
    reports: {
      title: 'Reports & Exports',
      description:
        'Generate contribution reports, dividend analytics, payroll reconciliation summaries, and board packs.',
      accent: '#f59e0b',
    },
  }[activeAction];

  const quickActions = [
    { id: 'members', label: 'Approve Members', icon: UserPlus },
    { id: 'loans', label: 'Process Loans', icon: HandCoins },
    { id: 'payroll', label: 'Upload Payroll', icon: Upload },
    { id: 'products', label: 'Manage Products', icon: BriefcaseBusiness },
    { id: 'elections', label: 'Run Elections', icon: Vote },
    { id: 'reports', label: 'Export Reports', icon: Download },
  ];

  const navItems = [
    { id: 'overview', label: 'Overview', icon: LayoutDashboard },
    { id: 'members', label: 'Members', icon: Users },
    { id: 'loans', label: 'Loans', icon: HandCoins },
    { id: 'payroll', label: 'Payroll', icon: FileSpreadsheet },
    { id: 'finance', label: 'Finance', icon: Wallet },
    { id: 'investments', label: 'Investments', icon: BriefcaseBusiness },
    { id: 'elections', label: 'Elections', icon: Vote },
    { id: 'compliance', label: 'Compliance', icon: ShieldCheck },
  ];

  const sectionMeta = {
    overview: {
      title: 'Admin Dashboard',
      subtitle:
        'Monitor members, payroll deductions, loans, dividends, products, elections, and compliance from one control panel.',
      badge: 'Admin systems online',
    },
    members: {
      title: 'Members Management',
      subtitle:
        'Approve onboarding, review KYC, segment membership activity, and manage member growth.',
      badge: 'Onboarding queue live',
    },
    loans: {
      title: 'Loans Desk',
      subtitle:
        'Review approval queues, disbursement readiness, loan types, and repayment exposure.',
      badge: 'Loan workflow active',
    },
    payroll: {
      title: 'Payroll Reconciliation',
      subtitle:
        'Upload deductions, reconcile payroll files, track failed rows, and confirm cooperative postings.',
      badge: 'Payroll sync ready',
    },
    finance: {
      title: 'Finance Overview',
      subtitle:
        'Track collections, loan exposure, dividend pool, commission, and cooperative liquidity.',
      badge: 'Financials updated',
    },
    investments: {
      title: 'Investments & Products',
      subtitle:
        'Manage cooperative-backed products, subscriptions, asset pools, and share windows.',
      badge: '3 live products',
    },
    elections: {
      title: 'Elections Center',
      subtitle:
        'Run executive polls, track turnout, monitor voting status, and prepare result publications.',
      badge: 'Voting monitored',
    },
    compliance: {
      title: 'Compliance & Audit',
      subtitle:
        'Review access controls, audit trails, KYC readiness, and risk monitoring tools.',
      badge: 'Compliance strong',
    },
  };

  const currentSection = sectionMeta[activeSection];
  const CurrentSectionIcon = sectionIcons[activeSection];

  const goToSection = (id) => {
    setActiveSection(id);
    if (isTablet) setSidebarOpen(false);
  };

  const renderActionPanel = () => {
    if (activeAction === 'members') {
      return (
        <div style={styles.actionStack}>
          <div style={styles.infoBannerGreen}>
            <UserCheck size={16} />
            <span>{pendingMembers.length} registrations awaiting review.</span>
          </div>
          <div style={{ ...styles.formGrid, ...(isMobile ? styles.formGridMobile : {}) }}>
            <div style={styles.miniInfoCard}>
              <span style={styles.miniInfoLabel}>Pending</span>
              <strong style={styles.miniInfoValue}>{pendingMembers.length}</strong>
            </div>
            <div style={styles.miniInfoCard}>
              <span style={styles.miniInfoLabel}>Active Members</span>
              <strong style={styles.miniInfoValue}>{activeMembers}</strong>
            </div>
          </div>
          <button style={styles.primaryButton}>Open Approval Queue</button>
        </div>
      );
    }

    if (activeAction === 'loans') {
      return (
        <div style={styles.actionStack}>
          <div style={styles.infoBannerTeal}>
            <HandCoins size={16} />
            <span>{loanRequests.length} loan requests in active workflow.</span>
          </div>
          <div style={{ ...styles.formGrid, ...(isMobile ? styles.formGridMobile : {}) }}>
            <div style={styles.miniInfoCard}>
              <span style={styles.miniInfoLabel}>Pending Disbursement</span>
              <strong style={styles.miniInfoValue}>
                {formatCurrency(pendingDisbursement)}
              </strong>
            </div>
            <div style={styles.miniInfoCard}>
              <span style={styles.miniInfoLabel}>Interest Rate</span>
              <strong style={styles.miniInfoValue}>{interestRate}%</strong>
            </div>
          </div>
          <button style={styles.primaryButton}>Open Loan Desk</button>
        </div>
      );
    }

    if (activeAction === 'payroll') {
      return (
        <div style={styles.actionStack}>
          <div style={styles.infoBannerBlue}>
            <Upload size={16} />
            <span>{payrollUpload.fileName} is ready for reconciliation.</span>
          </div>
          <div style={{ ...styles.formGrid, ...(isMobile ? styles.formGridMobile : {}) }}>
            <div style={styles.miniInfoCard}>
              <span style={styles.miniInfoLabel}>Records</span>
              <strong style={styles.miniInfoValue}>{payrollUpload.records}</strong>
            </div>
            <div style={styles.miniInfoCard}>
              <span style={styles.miniInfoLabel}>Processed</span>
              <strong style={styles.miniInfoValue}>{payrollUpload.processed}</strong>
            </div>
          </div>
          <button style={styles.primaryButton}>Upload Payroll File</button>
        </div>
      );
    }

    if (activeAction === 'products') {
      return (
        <div style={styles.actionStack}>
          <div style={styles.infoBannerPurple}>
            <Sparkles size={16} />
            <span>{products.length} cooperative products currently active.</span>
          </div>
          <button style={styles.primaryButton}>Create New Product</button>
        </div>
      );
    }

    if (activeAction === 'elections') {
      return (
        <div style={styles.actionStack}>
          <div style={styles.infoBannerPink}>
            <Vote size={16} />
            <span>{elections.length} election and poll items available.</span>
          </div>
          <button style={styles.primaryButton}>Launch Election</button>
        </div>
      );
    }

    return (
      <div style={styles.actionStack}>
        <div style={styles.infoBannerOrange}>
          <Download size={16} />
          <span>Export ready for contribution, dividend, and audit reports.</span>
        </div>
        <button style={styles.primaryButton}>Generate Report Pack</button>
      </div>
    );
  };

  const renderSectionHero = (stats = []) => (
    <motion.section
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.28 }}
      style={{
        ...styles.pageHero,
        ...(isTablet ? styles.pageHeroTablet : {}),
      }}
    >
      <div style={styles.pageHeroLeft}>
        <div style={styles.pageHeroBadge}>
          <CurrentSectionIcon size={13} />
          {currentSection.badge}
        </div>
        <h1 style={{ ...styles.pageHeroTitle, ...(isMobile ? styles.pageHeroTitleMobile : {}) }}>
          {currentSection.title}
        </h1>
        <p style={styles.pageHeroText}>{currentSection.subtitle}</p>
      </div>

      {!!stats.length && (
        <div
          style={{
            ...styles.pageHeroStats,
            ...(isMobile ? styles.pageHeroStatsMobile : {}),
          }}
        >
          {stats.map((item) => (
            <div key={item.label} style={styles.pageHeroStatCard}>
              <span style={styles.pageHeroStatLabel}>{item.label}</span>
              <strong style={styles.pageHeroStatValue}>{item.value}</strong>
            </div>
          ))}
        </div>
      )}
    </motion.section>
  );

  const renderMembersList = (items) => {
    if (isMobile) {
      return (
        <div style={styles.mobileList}>
          {items.length ? (
            items.map((member) => (
              <div key={member.id} style={styles.mobileCard}>
                <div style={styles.mobileCardTop}>
                  <div>
                    <div style={styles.mobileCardTitle}>{member.name}</div>
                    <div style={styles.mobileCardMeta}>
                      {member.id} • {member.department}
                    </div>
                  </div>
                  <span
                    style={{
                      ...styles.statusPill,
                      ...(member.status === 'Pending'
                        ? styles.statusPillWarning
                        : styles.statusPillInfo),
                    }}
                  >
                    {member.status}
                  </span>
                </div>

                <div style={styles.mobileKeyValue}>
                  <span>Contribution</span>
                  <strong>{formatCurrency(member.contribution)}</strong>
                </div>
                <div style={styles.mobileKeyValue}>
                  <span>KYC</span>
                  <strong>{member.kyc}</strong>
                </div>

                <div style={styles.inlineButtons}>
                  <button
                    onClick={() => approveMember(member.id)}
                    style={styles.primaryButton}
                  >
                    Approve
                  </button>
                  <button
                    onClick={() => rejectMember(member.id)}
                    style={styles.secondaryButton}
                  >
                    Reject
                  </button>
                </div>
              </div>
            ))
          ) : (
            <div style={styles.emptyState}>No pending members match your current search/filter.</div>
          )}
        </div>
      );
    }

    return (
      <div style={styles.tableWrap}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Member</th>
              <th style={styles.th}>Department</th>
              <th style={styles.th}>Contribution</th>
              <th style={styles.th}>Status</th>
              <th style={styles.th}>Joined</th>
              <th style={styles.th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.length ? (
              items.map((member) => (
                <tr key={member.id} style={styles.tr}>
                  <td style={styles.tdStrong}>
                    <div>{member.name}</div>
                    <div style={styles.subText}>{member.id}</div>
                  </td>
                  <td style={styles.td}>{member.department}</td>
                  <td style={styles.tdCredit}>{formatCurrency(member.contribution)}</td>
                  <td style={styles.td}>
                    <span
                      style={{
                        ...styles.statusPill,
                        ...(member.status === 'Pending'
                          ? styles.statusPillWarning
                          : styles.statusPillInfo),
                      }}
                    >
                      {member.status}
                    </span>
                  </td>
                  <td style={styles.td}>{member.joined}</td>
                  <td style={styles.td}>
                    <div style={styles.tableButtons}>
                      <button
                        onClick={() => approveMember(member.id)}
                        style={styles.tableApproveButton}
                      >
                        Approve
                      </button>
                      <button
                        onClick={() => rejectMember(member.id)}
                        style={styles.tableRejectButton}
                      >
                        Reject
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6" style={styles.emptyState}>
                  No pending members match your current search/filter.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    );
  };

  const renderLoansList = (items) => {
    if (isMobile) {
      return (
        <div style={styles.mobileList}>
          {items.length ? (
            items.map((loan) => (
              <div key={loan.id} style={styles.mobileCard}>
                <div style={styles.mobileCardTop}>
                  <div>
                    <div style={styles.mobileCardTitle}>{loan.memberName}</div>
                    <div style={styles.mobileCardMeta}>
                      {loan.id} • {loan.type}
                    </div>
                  </div>
                  <span
                    style={{
                      ...styles.statusPill,
                      ...(loan.status === 'Ready to Disburse'
                        ? styles.statusPillSuccess
                        : styles.statusPillWarning),
                    }}
                  >
                    {loan.status}
                  </span>
                </div>

                <div style={styles.mobileKeyValue}>
                  <span>Amount</span>
                  <strong>{formatCurrency(loan.amount)}</strong>
                </div>
                <div style={styles.mobileKeyValue}>
                  <span>Duration</span>
                  <strong>{loan.months} months</strong>
                </div>

                <div style={styles.inlineButtons}>
                  <button
                    onClick={() => approveLoan(loan.id)}
                    style={styles.primaryButton}
                  >
                    Approve
                  </button>
                  <button
                    onClick={() => rejectLoan(loan.id)}
                    style={styles.secondaryButton}
                  >
                    Reject
                  </button>
                </div>
              </div>
            ))
          ) : (
            <div style={styles.emptyState}>No loan requests match your current search/filter.</div>
          )}
        </div>
      );
    }

    return (
      <div style={styles.tableWrap}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Loan ID</th>
              <th style={styles.th}>Member</th>
              <th style={styles.th}>Type</th>
              <th style={styles.th}>Amount</th>
              <th style={styles.th}>Duration</th>
              <th style={styles.th}>Status</th>
              <th style={styles.th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.length ? (
              items.map((loan) => (
                <tr key={loan.id} style={styles.tr}>
                  <td style={styles.tdStrong}>
                    <div>{loan.id}</div>
                    <div style={styles.subText}>{loan.score} score</div>
                  </td>
                  <td style={styles.td}>
                    <div>{loan.memberName}</div>
                    <div style={styles.subText}>{loan.memberId}</div>
                  </td>
                  <td style={styles.td}>{loan.type}</td>
                  <td style={styles.tdCredit}>{formatCurrency(loan.amount)}</td>
                  <td style={styles.td}>{loan.months} months</td>
                  <td style={styles.td}>
                    <span
                      style={{
                        ...styles.statusPill,
                        ...(loan.status === 'Ready to Disburse'
                          ? styles.statusPillSuccess
                          : styles.statusPillWarning),
                      }}
                    >
                      {loan.status}
                    </span>
                  </td>
                  <td style={styles.td}>
                    <div style={styles.tableButtons}>
                      <button
                        onClick={() => approveLoan(loan.id)}
                        style={styles.tableApproveButton}
                      >
                        Approve
                      </button>
                      <button
                        onClick={() => rejectLoan(loan.id)}
                        style={styles.tableRejectButton}
                      >
                        Reject
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="7" style={styles.emptyState}>
                  No loan requests match your current search/filter.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    );
  };

  const renderOverviewSection = () => (
    <>
      <motion.section
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.04, duration: 0.28 }}
        style={{
          ...styles.hero,
          ...(isTablet ? styles.heroTablet : {}),
        }}
      >
        <div style={styles.heroLeft}>
          <div style={styles.heroBadge}>
            <Sparkles size={13} />
            Cooperative administration system online
          </div>
          <h1 style={{ ...styles.heroTitle, ...(isMobile ? styles.heroTitleMobile : {}) }}>
            Welcome back, {user?.name || 'Admin'}
          </h1>
          <p style={styles.heroText}>
            You have pending onboarding approvals, loan reviews, payroll reconciliation
            tasks, and report exports waiting this cycle.
          </p>

          <div style={styles.heroMetrics}>
            <div style={styles.heroMetricItem}>
              <span style={styles.heroMetricLabel}>Monthly Collections</span>
              <strong style={styles.heroMetricValue}>
                {formatCurrency(monthlyCollections)}
              </strong>
            </div>
            <div style={styles.heroMetricItem}>
              <span style={styles.heroMetricLabel}>Total Members</span>
              <strong style={styles.heroMetricValue}>{totalMembers}</strong>
            </div>
          </div>
        </div>

        <div
          style={{
            ...styles.heroRight,
            ...(isMobile ? styles.heroRightMobile : {}),
          }}
        >
          <motion.div whileHover={{ y: -3 }} transition={{ duration: 0.2 }} style={styles.heroGlassCard}>
            <div style={styles.heroCardTop}>
              <span style={styles.heroCardLabel}>Pending Loans</span>
              <HandCoins size={16} />
            </div>
            <div style={styles.heroCardAmount}>{loanRequests.length}</div>
            <div style={styles.heroCardMeta}>Needs action today</div>
          </motion.div>

          <motion.div whileHover={{ y: -3 }} transition={{ duration: 0.2 }} style={styles.heroGlassCardSecondary}>
            <div style={styles.heroCardTop}>
              <span style={styles.heroCardLabel}>Dividend Pool</span>
              <CircleDollarSign size={16} />
            </div>
            <div style={styles.heroCardAmount}>{formatCurrency(dividendPool)}</div>
            <div style={styles.heroCardMeta}>Ready for annual allocation</div>
          </motion.div>
        </div>
      </motion.section>

      <section
        style={{
          ...styles.statsGrid,
          ...(isMobile ? styles.statsGridMobile : {}),
        }}
      >
        {[
          {
            title: 'Active Members',
            value: activeMembers,
            sub: 'Currently contributing members',
            icon: Users,
            accent: '#16a34a',
          },
          {
            title: 'Loan Portfolio',
            value: formatCurrency(totalLoanPortfolio),
            sub: 'Outstanding cooperative exposure',
            icon: CreditCard,
            accent: '#0f766e',
          },
          {
            title: 'Developer Commission',
            value: formatCurrency(devCommission),
            sub: 'Current cycle service fee',
            icon: Receipt,
            accent: '#7c3aed',
          },
          {
            title: 'Payroll Completion',
            value: `${payrollCompletion}%`,
            sub: 'Monthly reconciliation status',
            icon: FileSpreadsheet,
            accent: '#2563eb',
          },
        ].map((card) => {
          const Icon = card.icon;
          return (
            <div key={card.title} style={styles.statCard}>
              <div
                style={{
                  ...styles.statIconWrap,
                  background: `linear-gradient(135deg, ${card.accent}, #0f172a)`,
                }}
              >
                <Icon size={16} color="#fff" />
              </div>
              <div style={styles.statTitle}>{card.title}</div>
              <div style={styles.statValue}>{card.value}</div>
              <div style={styles.statSub}>{card.sub}</div>
            </div>
          );
        })}
      </section>

      <section
        style={{
          ...styles.contentGrid,
          ...(isTablet ? styles.contentGridTablet : {}),
        }}
      >
        <div style={styles.leftColumn}>
          <motion.section initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} style={styles.card}>
            <div style={styles.cardHeader}>
              <div>
                <div style={styles.cardTitle}>Quick Actions</div>
                <div style={styles.cardSub}>
                  High-frequency administration actions for daily operations.
                </div>
              </div>
            </div>

            <div
              style={{
                ...styles.quickActionsGrid,
                ...(isMobile ? styles.quickActionsGridMobile : {}),
              }}
            >
              {quickActions.map((item) => {
                const Icon = item.icon;
                const active = activeAction === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => setActiveAction(item.id)}
                    style={{
                      ...styles.quickActionButton,
                      ...(active ? styles.quickActionButtonActive : {}),
                    }}
                  >
                    <div
                      style={{
                        ...styles.quickActionIcon,
                        ...(active ? styles.quickActionIconActive : {}),
                      }}
                    >
                      <Icon size={16} />
                    </div>
                    <span>{item.label}</span>
                  </button>
                );
              })}
            </div>

            <motion.div
              key={activeAction}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              style={{
                ...styles.actionPanel,
                borderColor: `${currentActionConfig.accent}22`,
              }}
            >
              <div style={styles.actionPanelTop}>
                <div>
                  <div style={styles.actionTitle}>{currentActionConfig.title}</div>
                  <div style={styles.actionDescription}>
                    {currentActionConfig.description}
                  </div>
                </div>
                <div
                  style={{
                    ...styles.actionDot,
                    backgroundColor: currentActionConfig.accent,
                  }}
                />
              </div>

              {renderActionPanel()}
            </motion.div>
          </motion.section>

          <motion.section initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} style={styles.card}>
            <div style={styles.cardHeaderSplit}>
              <div>
                <div style={styles.cardTitle}>Pending Member Registrations</div>
                <div style={styles.cardSub}>
                  Review onboarding requests and confirm KYC details.
                </div>
              </div>

              <div style={styles.filterWrap}>
                {['all', 'Pending', 'KYC Review'].map((type) => (
                  <button
                    key={type}
                    onClick={() => setMemberFilter(type)}
                    style={{
                      ...styles.filterButton,
                      ...(memberFilter === type ? styles.filterButtonActive : {}),
                    }}
                  >
                    {type}
                  </button>
                ))}
              </div>
            </div>

            {renderMembersList(filteredMembers.slice(0, isMobile ? 3 : 4))}
          </motion.section>

          <motion.section initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} style={styles.card}>
            <div style={styles.cardHeaderSplit}>
              <div>
                <div style={styles.cardTitle}>Loan Approval Queue</div>
                <div style={styles.cardSub}>
                  Review approval level, exposure, and readiness to disburse.
                </div>
              </div>

              <div style={styles.filterWrap}>
                {['all', 'admin', 'committee', 'ready'].map((type) => (
                  <button
                    key={type}
                    onClick={() => setLoanFilter(type)}
                    style={{
                      ...styles.filterButton,
                      ...(loanFilter === type ? styles.filterButtonActive : {}),
                    }}
                  >
                    {type}
                  </button>
                ))}
              </div>
            </div>

            {renderLoansList(filteredLoans.slice(0, isMobile ? 3 : 4))}
          </motion.section>
        </div>

        <div style={styles.rightColumn}>
          <motion.section initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} style={styles.card}>
            <div style={styles.cardHeader}>
              <div>
                <div style={styles.cardTitle}>Payroll Reconciliation</div>
                <div style={styles.cardSub}>
                  Track deduction uploads, processed rows, and unresolved records.
                </div>
              </div>
              <Upload size={16} color="#2563eb" />
            </div>

            <div style={styles.goalAmount}>{payrollCompletion}%</div>
            <div style={styles.goalLabel}>File: {payrollUpload.fileName}</div>

            <div style={styles.progressTrack}>
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${payrollCompletion}%` }}
                transition={{ duration: 0.8 }}
                style={styles.progressFillBlue}
              />
            </div>

            <div style={styles.progressMeta}>
              <span>{payrollUpload.processed} processed</span>
              <span>{payrollUpload.pending} pending</span>
            </div>
          </motion.section>

          <motion.section initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} style={styles.card}>
            <div style={styles.cardHeader}>
              <div>
                <div style={styles.cardTitle}>Recent Activity</div>
                <div style={styles.cardSub}>
                  Timeline of system events and operational updates.
                </div>
              </div>
              <Clock3 size={16} color="#f59e0b" />
            </div>

            <div style={styles.announcementList}>
              {activityLog.map((item) => (
                <div key={item.title} style={styles.announcementItem}>
                  <div
                    style={{
                      ...styles.announcementIcon,
                      ...(item.type === 'warning'
                        ? styles.announcementIconWarning
                        : item.type === 'info'
                        ? styles.announcementIconBlue
                        : {}),
                    }}
                  >
                    {item.type === 'warning' ? (
                      <AlertTriangle size={15} />
                    ) : item.type === 'info' ? (
                      <Filter size={15} />
                    ) : (
                      <CheckCircle2 size={15} />
                    )}
                  </div>
                  <div>
                    <div style={styles.announcementTitle}>{item.title}</div>
                    <div style={styles.announcementText}>{item.meta}</div>
                    <div style={styles.activityTime}>{item.time}</div>
                  </div>
                </div>
              ))}
            </div>
          </motion.section>

          <motion.section initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} style={styles.card}>
            <div style={styles.cardHeader}>
              <div>
                <div style={styles.cardTitle}>Admin Notices</div>
                <div style={styles.cardSub}>
                  Priority updates for cooperative leadership.
                </div>
              </div>
              <Building2 size={16} color="#16a34a" />
            </div>

            <div style={styles.announcementList}>
              {[
                {
                  title: 'Monthly board meeting scheduled',
                  desc: 'Financial and dividend review fixed for Friday, 10:00 AM.',
                  icon: CalendarDays,
                },
                {
                  title: '24 unresolved payroll records',
                  desc: 'HR reconciliation required before settlement closure.',
                  icon: AlertTriangle,
                },
                {
                  title: 'Committee loan session pending',
                  desc: '1 high-value loan waiting for final committee decision.',
                  icon: ClipboardCheck,
                },
              ].map((item) => {
                const Icon = item.icon;
                return (
                  <div key={item.title} style={styles.announcementItem}>
                    <div style={styles.announcementIcon}>
                      <Icon size={15} />
                    </div>
                    <div>
                      <div style={styles.announcementTitle}>{item.title}</div>
                      <div style={styles.announcementText}>{item.desc}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </motion.section>
        </div>
      </section>
    </>
  );

  const renderMembersSection = () => (
    <>
      {renderSectionHero([
        { label: 'Total Members', value: totalMembers },
        { label: 'Pending Review', value: filteredMembers.length },
        { label: 'Active Ratio', value: `${Math.round((activeMembers / totalMembers) * 100)}%` },
      ])}

      <section
        style={{
          ...styles.contentGrid,
          ...(isTablet ? styles.contentGridTablet : {}),
        }}
      >
        <div style={styles.leftColumn}>
          <motion.section initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} style={styles.card}>
            <div style={styles.cardHeaderSplit}>
              <div>
                <div style={styles.cardTitle}>Onboarding Queue</div>
                <div style={styles.cardSub}>
                  Review registration requests, validate KYC, and activate member accounts.
                </div>
              </div>

              <div style={styles.filterWrap}>
                {['all', 'Pending', 'KYC Review'].map((type) => (
                  <button
                    key={type}
                    onClick={() => setMemberFilter(type)}
                    style={{
                      ...styles.filterButton,
                      ...(memberFilter === type ? styles.filterButtonActive : {}),
                    }}
                  >
                    {type}
                  </button>
                ))}
              </div>
            </div>

            {renderMembersList(filteredMembers)}
          </motion.section>
        </div>

        <div style={styles.rightColumn}>
          <motion.section initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} style={styles.card}>
            <div style={styles.cardHeader}>
              <div>
                <div style={styles.cardTitle}>Membership Segments</div>
                <div style={styles.cardSub}>
                  Quick distribution view across active, inactive, and pending groups.
                </div>
              </div>
              <Layers3 size={16} color="#16a34a" />
            </div>

            <div style={styles.metricStack}>
              {memberSegments.map((item) => (
                <div key={item.label} style={styles.metricChip}>
                  <span>{item.label}</span>
                  <strong>{item.value}</strong>
                </div>
              ))}
            </div>
          </motion.section>
        </div>
      </section>
    </>
  );

  const renderLoansSection = () => (
    <>
      {renderSectionHero([
        { label: 'Open Requests', value: filteredLoans.length },
        { label: 'Pending Disbursement', value: formatCurrency(pendingDisbursement) },
        { label: 'Interest Rate', value: `${interestRate}%` },
      ])}

      <section
        style={{
          ...styles.contentGrid,
          ...(isTablet ? styles.contentGridTablet : {}),
        }}
      >
        <div style={styles.leftColumn}>
          <motion.section initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} style={styles.card}>
            <div style={styles.cardHeaderSplit}>
              <div>
                <div style={styles.cardTitle}>Loan Queue</div>
                <div style={styles.cardSub}>
                  Filter by approval stage and manage loan decisions.
                </div>
              </div>

              <div style={styles.filterWrap}>
                {['all', 'admin', 'committee', 'ready'].map((type) => (
                  <button
                    key={type}
                    onClick={() => setLoanFilter(type)}
                    style={{
                      ...styles.filterButton,
                      ...(loanFilter === type ? styles.filterButtonActive : {}),
                    }}
                  >
                    {type}
                  </button>
                ))}
              </div>
            </div>

            {renderLoansList(filteredLoans)}
          </motion.section>
        </div>

        <div style={styles.rightColumn}>
          <motion.section initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} style={styles.card}>
            <div style={styles.cardHeader}>
              <div>
                <div style={styles.cardTitle}>Loan Rule Settings</div>
                <div style={styles.cardSub}>
                  Adjust lending policy and approval parameters.
                </div>
              </div>
              <Settings2 size={16} color="#15803d" />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Interest Rate (%)</label>
              <input
                type="number"
                value={interestRate}
                onChange={(e) => setInterestRate(e.target.value)}
                style={styles.inputField}
              />
            </div>

            <div style={{ ...styles.formGrid, ...(isMobile ? styles.formGridMobile : {}) }}>
              <div style={styles.miniInfoCard}>
                <span style={styles.miniInfoLabel}>Max Tenure</span>
                <strong style={styles.miniInfoValue}>36 Months</strong>
              </div>
              <div style={styles.miniInfoCard}>
                <span style={styles.miniInfoLabel}>Loan Ratio</span>
                <strong style={styles.miniInfoValue}>3x Savings</strong>
              </div>
            </div>

            <button style={styles.primaryButton}>Update Lending Rules</button>
          </motion.section>
        </div>
      </section>
    </>
  );

  const renderPayrollSection = () => (
    <>
      {renderSectionHero([
        { label: 'Upload File', value: payrollUpload.fileName },
        { label: 'Processed', value: payrollUpload.processed },
        { label: 'Pending Rows', value: payrollUpload.pending },
      ])}

      <section
        style={{
          ...styles.contentGrid,
          ...(isTablet ? styles.contentGridTablet : {}),
        }}
      >
        <div style={styles.leftColumn}>
          <motion.section initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} style={styles.card}>
            <div style={styles.cardHeader}>
              <div>
                <div style={styles.cardTitle}>Payroll Upload Summary</div>
                <div style={styles.cardSub}>
                  Deduction file status, processing progress, and reconciliation outcome.
                </div>
              </div>
              <Upload size={16} color="#2563eb" />
            </div>

            <div style={styles.metricStack}>
              <div style={styles.metricChip}>
                <span>Source File</span>
                <strong>{payrollUpload.fileName}</strong>
              </div>
              <div style={styles.metricChip}>
                <span>Total Records</span>
                <strong>{payrollUpload.records}</strong>
              </div>
              <div style={styles.metricChip}>
                <span>Processed</span>
                <strong>{payrollUpload.processed}</strong>
              </div>
              <div style={styles.metricChip}>
                <span>Pending</span>
                <strong>{payrollUpload.pending}</strong>
              </div>
              <div style={styles.metricChip}>
                <span>Failed</span>
                <strong>{payrollUpload.failed}</strong>
              </div>
            </div>
          </motion.section>
        </div>

        <div style={styles.rightColumn}>
          <motion.section initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} style={styles.card}>
            <div style={styles.cardHeader}>
              <div>
                <div style={styles.cardTitle}>Reconciliation Health</div>
                <div style={styles.cardSub}>
                  Monthly payroll deduction completion score.
                </div>
              </div>
              <Activity size={16} color="#0f766e" />
            </div>

            <div style={styles.goalAmount}>{payrollCompletion}%</div>
            <div style={styles.goalLabel}>Processing completion rate</div>

            <div style={styles.progressTrack}>
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${payrollCompletion}%` }}
                transition={{ duration: 0.8 }}
                style={styles.progressFillBlue}
              />
            </div>

            <div style={styles.progressMeta}>
              <span>Reconciled rows</span>
              <span>{payrollUpload.processed}/{payrollUpload.records}</span>
            </div>

            <button style={styles.primaryButton}>Upload New File</button>
          </motion.section>
        </div>
      </section>
    </>
  );

  const renderFinanceSection = () => (
    <>
      {renderSectionHero([
        { label: 'Collections', value: formatCurrency(monthlyCollections) },
        { label: 'Portfolio', value: formatCurrency(totalLoanPortfolio) },
        { label: 'Dividend Pool', value: formatCurrency(dividendPool) },
      ])}

      <section
        style={{
          ...styles.contentGrid,
          ...(isTablet ? styles.contentGridTablet : {}),
        }}
      >
        <div style={styles.leftColumn}>
          <motion.section initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} style={styles.card}>
            <div style={styles.cardHeader}>
              <div>
                <div style={styles.cardTitle}>Financial Snapshot</div>
                <div style={styles.cardSub}>
                  Current cycle values across collections, loans, dividends, and service fees.
                </div>
              </div>
              <Banknote size={16} color="#16a34a" />
            </div>

            <div
              style={{
                ...styles.financeGrid,
                ...(isMobile ? styles.financeGridMobile : {}),
              }}
            >
              {financeCards.map((item) => {
                const Icon = item.icon;
                return (
                  <div key={item.title} style={styles.financeCard}>
                    <div style={styles.financeCardIcon}>
                      <Icon size={16} />
                    </div>
                    <div style={styles.financeCardTitle}>{item.title}</div>
                    <div style={styles.financeCardValue}>{item.value}</div>
                  </div>
                );
              })}
            </div>
          </motion.section>
        </div>

        <div style={styles.rightColumn}>
          <motion.section initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} style={styles.card}>
            <div style={styles.cardHeader}>
              <div>
                <div style={styles.cardTitle}>Finance Exports</div>
                <div style={styles.cardSub}>
                  Generate statements, analytics packs, and board reports.
                </div>
              </div>
              <FileBarChart size={16} color="#7c3aed" />
            </div>

            <div style={styles.scheduleList}>
              {recentReports.map((item) => (
                <div key={item.title} style={styles.scheduleItem}>
                  <div>
                    <div style={styles.scheduleTitle}>{item.title}</div>
                    <div style={styles.scheduleText}>{item.date}</div>
                  </div>
                  <span style={styles.scheduleTag}>{item.status}</span>
                </div>
              ))}
            </div>

            <button style={styles.primaryButton}>Export Finance Pack</button>
          </motion.section>
        </div>
      </section>
    </>
  );

  const renderInvestmentsSection = () => (
    <>
      {renderSectionHero([
        { label: 'Live Products', value: products.length },
        { label: 'Subscribers', value: products.reduce((a, b) => a + b.subscribers, 0) },
        { label: 'Featured ROI', value: '14% Annual' },
      ])}

      <motion.section initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} style={styles.card}>
        <div style={styles.cardHeader}>
          <div>
            <div style={styles.cardTitle}>Products & Investment Windows</div>
            <div style={styles.cardSub}>
              Manage subscriptions, live offerings, and cooperative-backed assets.
            </div>
          </div>
        </div>

        <div
          style={{
            ...styles.productsGrid,
            ...(isMobile ? styles.productsGridMobile : {}),
          }}
        >
          {products.map((product) => (
            <div key={product.title} style={styles.productCard}>
              <div style={styles.productCardTop}>
                <span style={styles.productBadge}>{product.status}</span>
                <BriefcaseBusiness size={16} />
              </div>
              <div style={styles.productTitle}>{product.title}</div>
              <div style={styles.productMeta}>{product.roi}</div>
              <div style={styles.productText}>Subscribers: {product.subscribers}</div>
              <div style={styles.productText}>{product.units}</div>
              <div style={{ height: 12 }} />
              <button style={styles.secondaryButton}>Manage Product</button>
            </div>
          ))}
        </div>
      </motion.section>
    </>
  );

  const renderElectionsSection = () => (
    <>
      {renderSectionHero([
        { label: 'Election Items', value: elections.length },
        { label: 'Highest Turnout', value: '74%' },
        { label: 'Live Polls', value: elections.filter((e) => e.status === 'Live').length },
      ])}

      <section
        style={{
          ...styles.contentGrid,
          ...(isTablet ? styles.contentGridTablet : {}),
        }}
      >
        <div style={styles.leftColumn}>
          <motion.section initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} style={styles.card}>
            <div style={styles.cardHeader}>
              <div>
                <div style={styles.cardTitle}>Election Monitor</div>
                <div style={styles.cardSub}>
                  Track turnout, voting status, and participation for elections and polls.
                </div>
              </div>
              <Vote size={16} color="#db2777" />
            </div>

            <div style={styles.announcementList}>
              {elections.map((election) => (
                <div key={election.title} style={styles.announcementItem}>
                  <div style={styles.announcementIconPink}>
                    <Vote size={15} />
                  </div>
                  <div>
                    <div style={styles.announcementTitle}>{election.title}</div>
                    <div style={styles.announcementText}>
                      Turnout: {election.turnout} • Voters: {election.voters} • {election.status}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </motion.section>
        </div>

        <div style={styles.rightColumn}>
          <motion.section initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} style={styles.card}>
            <div style={styles.cardHeader}>
              <div>
                <div style={styles.cardTitle}>Election Tools</div>
                <div style={styles.cardSub}>
                  Launch new polls, prepare voter lists, and publish results.
                </div>
              </div>
              <BadgeCheck size={16} color="#16a34a" />
            </div>

            <div style={styles.metricStack}>
              <div style={styles.metricChip}>
                <span>Secure Ballot</span>
                <strong>Enabled</strong>
              </div>
              <div style={styles.metricChip}>
                <span>Results Visibility</span>
                <strong>Admin Controlled</strong>
              </div>
              <div style={styles.metricChip}>
                <span>Voter Sync</span>
                <strong>Member Based</strong>
              </div>
            </div>

            <button style={styles.primaryButton}>Launch New Election</button>
          </motion.section>
        </div>
      </section>
    </>
  );

  const renderComplianceSection = () => (
    <>
      {renderSectionHero([
        { label: 'Compliance Score', value: `${complianceScore}%` },
        { label: 'Audit Controls', value: 'Enabled' },
        { label: 'KYC Policy', value: 'Active' },
      ])}

      <section
        style={{
          ...styles.contentGrid,
          ...(isTablet ? styles.contentGridTablet : {}),
        }}
      >
        <div style={styles.leftColumn}>
          <motion.section initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} style={styles.card}>
            <div style={styles.cardHeader}>
              <div>
                <div style={styles.cardTitle}>Compliance Overview</div>
                <div style={styles.cardSub}>
                  Risk controls, audit readiness, and regulatory hygiene indicators.
                </div>
              </div>
              <ShieldAlert size={16} color="#0f766e" />
            </div>

            <div style={styles.goalAmount}>{complianceScore}%</div>
            <div style={styles.goalLabel}>Operational compliance score</div>

            <div style={styles.progressTrack}>
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${complianceScore}%` }}
                transition={{ duration: 0.8 }}
                style={styles.progressFillTeal}
              />
            </div>

            <div style={styles.noteList}>
              {complianceItems.map((item) => (
                <div key={item} style={styles.noteItem}>
                  {item}
                </div>
              ))}
            </div>
          </motion.section>
        </div>

        <div style={styles.rightColumn}>
          <motion.section initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} style={styles.card}>
            <div style={styles.cardHeader}>
              <div>
                <div style={styles.cardTitle}>Audit Trail</div>
                <div style={styles.cardSub}>
                  Governance items recently captured by the system.
                </div>
              </div>
              <ShieldCheck size={16} color="#16a34a" />
            </div>

            <div style={styles.announcementList}>
              {activityLog.map((item) => (
                <div key={item.title} style={styles.announcementItem}>
                  <div style={styles.announcementIcon}>
                    <CheckCircle2 size={15} />
                  </div>
                  <div>
                    <div style={styles.announcementTitle}>{item.title}</div>
                    <div style={styles.announcementText}>{item.meta}</div>
                  </div>
                </div>
              ))}
            </div>
          </motion.section>
        </div>
      </section>
    </>
  );

  const renderSectionContent = () => {
    switch (activeSection) {
      case 'members':
        return renderMembersSection();
      case 'loans':
        return renderLoansSection();
      case 'payroll':
        return renderPayrollSection();
      case 'finance':
        return renderFinanceSection();
      case 'investments':
        return renderInvestmentsSection();
      case 'elections':
        return renderElectionsSection();
      case 'compliance':
        return renderComplianceSection();
      case 'overview':
      default:
        return renderOverviewSection();
    }
  };

  const sidebar = (
    <motion.aside
      initial={{ x: -26, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      transition={{ duration: 0.3 }}
      style={{
        ...styles.sidebar,
        ...(isTablet ? styles.sidebarTablet : {}),
      }}
    >
      <div style={styles.brandWrap}>
        <div style={styles.logoBadge}>
          <Landmark size={18} />
        </div>
        <div>
          <div style={styles.brandTitle}>FAAN Cooperative</div>
          <div style={styles.brandSub}>Administrative Control Center</div>
        </div>
      </div>

      <div style={styles.adminCard}>
        <div style={styles.adminAvatar}>
          {(user?.name || 'A').slice(0, 1).toUpperCase()}
        </div>
        <div>
          <div style={styles.adminName}>{user?.name || 'Administrator'}</div>
          <div style={styles.adminMeta}>Cooperative Admin</div>
        </div>
      </div>

      <div style={styles.navWrap}>
        {navItems.map((item) => {
          const Icon = item.icon;
          const active = activeSection === item.id;

          return (
            <button
              key={item.id}
              onClick={() => goToSection(item.id)}
              style={{
                ...styles.navButton,
                ...(active ? styles.navButtonActive : {}),
              }}
            >
              <span style={styles.navLeft}>
                <Icon size={16} />
                {item.label}
              </span>
              <ChevronRight size={14} />
            </button>
          );
        })}
      </div>

      <div style={styles.sidebarBottomCard}>
        <div style={styles.sidebarBottomTitle}>Admin System Health</div>
        <div style={styles.sidebarBottomText}>
          Payroll, member onboarding, and audit systems are all running normally.
        </div>
      </div>
    </motion.aside>
  );

  return (
    <div style={styles.page}>
      <div style={styles.bgGlowOne} />
      <div style={styles.bgGlowTwo} />

      {isTablet && (
        <button onClick={() => setSidebarOpen(true)} style={styles.mobileMenuButton}>
          <Menu size={18} />
        </button>
      )}

      <AnimatePresence>
        {isTablet && sidebarOpen && (
          <>
            <motion.div
              style={styles.overlay}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSidebarOpen(false)}
            />
            <motion.div
              style={styles.mobileSidebarShell}
              initial={{ x: -320 }}
              animate={{ x: 0 }}
              exit={{ x: -320 }}
              transition={{ duration: 0.26 }}
            >
              <button onClick={() => setSidebarOpen(false)} style={styles.closeButton}>
                <X size={16} />
              </button>
              {sidebar}
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {!isTablet && sidebar}

      <main
        style={{
          ...styles.main,
          ...(isTablet ? styles.mainTablet : {}),
        }}
      >
        <motion.header
          initial={{ y: -14, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.3 }}
          style={{
            ...styles.topbar,
            ...(isTablet ? styles.topbarTablet : {}),
          }}
        >
          <div style={styles.topbarTextWrap}>
            <div style={styles.pageTitle}>{currentSection.title}</div>
            <div style={styles.pageSubtitle}>{currentSection.subtitle}</div>
          </div>

          <div
            style={{
              ...styles.topbarActions,
              ...(isMobile ? styles.topbarActionsMobile : {}),
            }}
          >
            <div
              style={{
                ...styles.searchBox,
                ...(isMobile ? styles.searchBoxMobile : {}),
              }}
            >
              <Search size={16} color="#64748b" />
              <input
                placeholder="Search members, loans, or records"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={styles.searchInput}
              />
            </div>

            <button style={styles.iconButton}>
              <Bell size={16} />
            </button>

            <button onClick={logout} style={styles.logoutButton}>
              <LogOut size={15} />
              {!isMobile && 'Logout'}
            </button>
          </div>
        </motion.header>

        {renderSectionContent()}
      </main>
    </div>
  );
};

const styles = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    background:
      'radial-gradient(circle at top left, rgba(34,197,94,0.12), transparent 24%), linear-gradient(180deg, #f7fff9 0%, #f8fafc 100%)',
    position: 'relative',
    overflow: 'hidden',
    color: '#0f172a',
    fontFamily:
      'Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  },
  bgGlowOne: {
    position: 'absolute',
    top: -120,
    right: -80,
    width: 280,
    height: 280,
    background: 'rgba(34,197,94,0.18)',
    filter: 'blur(80px)',
    borderRadius: '50%',
    pointerEvents: 'none',
  },
  bgGlowTwo: {
    position: 'absolute',
    bottom: -120,
    left: -60,
    width: 260,
    height: 260,
    background: 'rgba(16,185,129,0.12)',
    filter: 'blur(90px)',
    borderRadius: '50%',
    pointerEvents: 'none',
  },

  sidebar: {
    width: 290,
    minWidth: 290,
    padding: 20,
    borderRight: '1px solid rgba(15,23,42,0.06)',
    background: 'rgba(255,255,255,0.76)',
    backdropFilter: 'blur(16px)',
    display: 'flex',
    flexDirection: 'column',
    gap: 18,
    position: 'relative',
    zIndex: 3,
  },
  sidebarTablet: {
    width: '100%',
    minWidth: '100%',
    borderRight: 'none',
    height: '100%',
  },
  mobileSidebarShell: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: 320,
    maxWidth: '88vw',
    height: '100vh',
    background: '#fff',
    zIndex: 60,
    boxShadow: '20px 0 60px rgba(15,23,42,0.18)',
  },
  mobileMenuButton: {
    position: 'fixed',
    top: 16,
    left: 16,
    zIndex: 25,
    width: 42,
    height: 42,
    borderRadius: 13,
    border: '1px solid rgba(15,23,42,0.08)',
    background: 'rgba(255,255,255,0.94)',
    backdropFilter: 'blur(12px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    boxShadow: '0 12px 30px rgba(15,23,42,0.08)',
  },
  closeButton: {
    position: 'absolute',
    top: 14,
    right: 14,
    width: 36,
    height: 36,
    borderRadius: 12,
    border: '1px solid rgba(15,23,42,0.08)',
    background: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2,
    cursor: 'pointer',
  },
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(2,6,23,0.36)',
    zIndex: 50,
  },

  brandWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    paddingBottom: 12,
    borderBottom: '1px solid rgba(15,23,42,0.06)',
  },
  logoBadge: {
    width: 40,
    height: 40,
    borderRadius: 13,
    background: 'linear-gradient(135deg, #16a34a, #064e3b)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    boxShadow: '0 14px 30px rgba(22,163,74,0.24)',
  },
  brandTitle: {
    fontSize: fs(11),
    fontWeight: 800,
    color: '#052e16',
  },
  brandSub: {
    fontSize: fs(8),
    color: '#64748b',
  },
  adminCard: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: 12,
    borderRadius: 16,
    background: 'linear-gradient(135deg, #effff4, #f8fafc)',
    border: '1px solid rgba(34,197,94,0.12)',
  },
  adminAvatar: {
    width: 46,
    height: 46,
    borderRadius: 15,
    background: 'linear-gradient(135deg, #16a34a, #15803d)',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 800,
    fontSize: fs(13),
  },
  adminName: {
    fontWeight: 700,
    fontSize: fs(11),
  },
  adminMeta: {
    fontSize: fs(8),
    color: '#64748b',
    marginTop: 2,
  },

  navWrap: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  navButton: {
    width: '100%',
    border: '1px solid transparent',
    background: 'transparent',
    borderRadius: 14,
    padding: '12px 12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',
    color: '#334155',
    transition: 'all 0.2s ease',
  },
  navButtonActive: {
    background: 'linear-gradient(135deg, #16a34a, #166534)',
    color: '#fff',
    boxShadow: '0 12px 28px rgba(22,163,74,0.22)',
  },
  navLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontWeight: 600,
    fontSize: fs(10),
  },

  sidebarBottomCard: {
    marginTop: 'auto',
    padding: 14,
    borderRadius: 16,
    background: '#0f172a',
    color: '#fff',
  },
  sidebarBottomTitle: {
    fontWeight: 700,
    fontSize: fs(10),
    marginBottom: 6,
  },
  sidebarBottomText: {
    fontSize: fs(8),
    lineHeight: 1.6,
    color: 'rgba(255,255,255,0.75)',
  },

  main: {
    flex: 1,
    padding: '24px 24px 28px',
    position: 'relative',
    zIndex: 2,
    overflowX: 'hidden',
  },
  mainTablet: {
    padding: '72px 16px 24px',
  },

  topbar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 14,
    flexWrap: 'wrap',
    marginBottom: 20,
  },
  topbarTablet: {
    flexDirection: 'column',
    alignItems: 'stretch',
  },
  topbarTextWrap: {
    minWidth: 0,
  },
  pageTitle: {
    fontSize: fs(20),
    fontWeight: 800,
    color: '#0f172a',
    marginBottom: 5,
  },
  pageSubtitle: {
    color: '#64748b',
    fontSize: fs(10),
    maxWidth: 720,
    lineHeight: 1.6,
  },
  topbarActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
  },
  topbarActionsMobile: {
    width: '100%',
  },
  searchBox: {
    minWidth: 240,
    height: 42,
    padding: '0 12px',
    borderRadius: 13,
    border: '1px solid rgba(15,23,42,0.08)',
    background: 'rgba(255,255,255,0.85)',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    boxShadow: '0 10px 30px rgba(15,23,42,0.04)',
  },
  searchBoxMobile: {
    minWidth: 0,
    width: 'calc(100% - 100px)',
    flex: 1,
  },
  searchInput: {
    border: 'none',
    outline: 'none',
    background: 'transparent',
    width: '100%',
    minWidth: 0,
    fontSize: fs(10),
    color: '#0f172a',
  },
  iconButton: {
    width: 42,
    height: 42,
    borderRadius: 13,
    border: '1px solid rgba(15,23,42,0.08)',
    background: 'rgba(255,255,255,0.88)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    flexShrink: 0,
  },
  logoutButton: {
    height: 42,
    padding: '0 14px',
    borderRadius: 13,
    border: 'none',
    background: '#0f172a',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    gap: 7,
    fontWeight: 700,
    fontSize: fs(10),
    cursor: 'pointer',
    boxShadow: '0 14px 30px rgba(15,23,42,0.15)',
    flexShrink: 0,
  },

  pageHero: {
    borderRadius: 24,
    padding: 20,
    marginBottom: 20,
    background: 'linear-gradient(135deg, rgba(5,150,105,0.95), rgba(15,23,42,0.94))',
    color: '#fff',
    display: 'grid',
    gridTemplateColumns: 'minmax(240px, 1.2fr) minmax(200px, 0.8fr)',
    gap: 14,
    boxShadow: '0 30px 70px rgba(5,150,105,0.18)',
  },
  pageHeroTablet: {
    gridTemplateColumns: '1fr',
  },
  pageHeroLeft: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  pageHeroBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 7,
    width: 'fit-content',
    padding: '7px 10px',
    borderRadius: 999,
    background: 'rgba(255,255,255,0.14)',
    fontSize: fs(8),
    fontWeight: 700,
  },
  pageHeroTitle: {
    margin: 0,
    fontSize: fs(21),
    lineHeight: 1.15,
    fontWeight: 800,
  },
  pageHeroTitleMobile: {
    fontSize: fs(17),
  },
  pageHeroText: {
    margin: 0,
    color: 'rgba(255,255,255,0.82)',
    lineHeight: 1.7,
    fontSize: fs(10),
  },
  pageHeroStats: {
    display: 'grid',
    gap: 10,
  },
  pageHeroStatsMobile: {
    gridTemplateColumns: '1fr',
  },
  pageHeroStatCard: {
    padding: 12,
    borderRadius: 16,
    background: 'rgba(255,255,255,0.12)',
  },
  pageHeroStatLabel: {
    display: 'block',
    fontSize: fs(8),
    color: 'rgba(255,255,255,0.72)',
    marginBottom: 5,
  },
  pageHeroStatValue: {
    fontSize: fs(11),
    fontWeight: 800,
    lineHeight: 1.45,
    wordBreak: 'break-word',
  },

  hero: {
    borderRadius: 24,
    padding: 20,
    marginBottom: 20,
    background: 'linear-gradient(135deg, rgba(5,150,105,0.95), rgba(15,23,42,0.94))',
    color: '#fff',
    display: 'grid',
    gridTemplateColumns: 'minmax(240px, 1.4fr) minmax(220px, 0.9fr)',
    gap: 14,
    boxShadow: '0 30px 70px rgba(5,150,105,0.22)',
  },
  heroTablet: {
    gridTemplateColumns: '1fr',
  },
  heroLeft: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  heroBadge: {
    display: 'inline-flex',
    width: 'fit-content',
    alignItems: 'center',
    gap: 7,
    padding: '7px 10px',
    borderRadius: 999,
    background: 'rgba(255,255,255,0.14)',
    fontSize: fs(8),
    fontWeight: 700,
  },
  heroTitle: {
    margin: 0,
    fontSize: fs(23),
    lineHeight: 1.15,
    fontWeight: 800,
  },
  heroTitleMobile: {
    fontSize: fs(18),
  },
  heroText: {
    margin: 0,
    maxWidth: 640,
    color: 'rgba(255,255,255,0.82)',
    lineHeight: 1.7,
    fontSize: fs(10),
  },
  heroMetrics: {
    display: 'flex',
    gap: 10,
    flexWrap: 'wrap',
    marginTop: 4,
  },
  heroMetricItem: {
    padding: '10px 12px',
    borderRadius: 16,
    background: 'rgba(255,255,255,0.12)',
    minWidth: 145,
    flex: '1 1 145px',
  },
  heroMetricLabel: {
    display: 'block',
    fontSize: fs(8),
    color: 'rgba(255,255,255,0.7)',
    marginBottom: 6,
  },
  heroMetricValue: {
    fontSize: fs(12),
    fontWeight: 800,
  },
  heroRight: {
    display: 'grid',
    gridTemplateRows: '1fr 1fr',
    gap: 12,
  },
  heroRightMobile: {
    gridTemplateRows: 'auto',
  },
  heroGlassCard: {
    borderRadius: 18,
    padding: 14,
    background: 'rgba(255,255,255,0.14)',
    backdropFilter: 'blur(12px)',
    border: '1px solid rgba(255,255,255,0.14)',
  },
  heroGlassCardSecondary: {
    borderRadius: 18,
    padding: 14,
    background: 'rgba(255,255,255,0.09)',
    backdropFilter: 'blur(12px)',
    border: '1px solid rgba(255,255,255,0.12)',
  },
  heroCardTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    color: 'rgba(255,255,255,0.82)',
    marginBottom: 14,
  },
  heroCardLabel: {
    fontSize: fs(9),
    fontWeight: 700,
  },
  heroCardAmount: {
    fontSize: fs(19),
    fontWeight: 900,
    marginBottom: 6,
  },
  heroCardMeta: {
    fontSize: fs(9),
    color: 'rgba(255,255,255,0.72)',
  },

  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
    gap: 14,
    marginBottom: 20,
  },
  statsGridMobile: {
    gridTemplateColumns: '1fr',
  },
  statCard: {
    background: 'rgba(255,255,255,0.88)',
    border: '1px solid rgba(15,23,42,0.06)',
    borderRadius: 18,
    padding: 16,
    boxShadow: '0 14px 40px rgba(15,23,42,0.05)',
  },
  statIconWrap: {
    width: 36,
    height: 36,
    borderRadius: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 12,
  },
  statTitle: {
    color: '#64748b',
    fontSize: fs(9),
    fontWeight: 600,
  },
  statValue: {
    fontSize: fs(19),
    fontWeight: 800,
    margin: '7px 0 5px',
    color: '#0f172a',
    wordBreak: 'break-word',
  },
  statSub: {
    color: '#94a3b8',
    fontSize: fs(8.5),
    lineHeight: 1.5,
  },

  contentGrid: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1.65fr) minmax(290px, 0.95fr)',
    gap: 16,
    alignItems: 'start',
  },
  contentGridTablet: {
    gridTemplateColumns: '1fr',
  },
  leftColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    minWidth: 0,
  },
  rightColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    minWidth: 0,
  },
  card: {
    background: 'rgba(255,255,255,0.9)',
    border: '1px solid rgba(15,23,42,0.06)',
    borderRadius: 20,
    padding: 18,
    boxShadow: '0 20px 60px rgba(15,23,42,0.05)',
    minWidth: 0,
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    marginBottom: 14,
    flexWrap: 'wrap',
  },
  cardHeaderSplit: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 14,
    marginBottom: 14,
    flexWrap: 'wrap',
  },
  cardTitle: {
    fontSize: fs(12.5),
    fontWeight: 800,
    color: '#0f172a',
    marginBottom: 3,
  },
  cardSub: {
    fontSize: fs(9.5),
    color: '#64748b',
    lineHeight: 1.6,
  },

  quickActionsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gap: 10,
    marginBottom: 16,
  },
  quickActionsGridMobile: {
    gridTemplateColumns: '1fr 1fr',
  },
  quickActionButton: {
    padding: 12,
    borderRadius: 16,
    border: '1px solid rgba(15,23,42,0.07)',
    background: '#fff',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    cursor: 'pointer',
    fontWeight: 700,
    fontSize: fs(9.5),
    color: '#0f172a',
    minWidth: 0,
  },
  quickActionButtonActive: {
    background: 'linear-gradient(135deg, #ecfdf5, #f0fdf4)',
    border: '1px solid rgba(22,163,74,0.18)',
    boxShadow: '0 14px 30px rgba(34,197,94,0.10)',
  },
  quickActionIcon: {
    width: 34,
    height: 34,
    borderRadius: 11,
    background: '#f8fafc',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#334155',
    flexShrink: 0,
  },
  quickActionIconActive: {
    background: '#16a34a',
    color: '#fff',
  },

  actionPanel: {
    border: '1px solid rgba(15,23,42,0.07)',
    background: 'linear-gradient(180deg, #ffffff, #f8fafc)',
    borderRadius: 18,
    padding: 16,
  },
  actionPanelTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 14,
    marginBottom: 16,
    flexWrap: 'wrap',
  },
  actionTitle: {
    fontSize: fs(12),
    fontWeight: 800,
    color: '#0f172a',
    marginBottom: 5,
  },
  actionDescription: {
    fontSize: fs(9.5),
    color: '#64748b',
    lineHeight: 1.6,
  },
  actionDot: {
    width: 10,
    height: 10,
    borderRadius: 999,
    marginTop: 4,
    flexShrink: 0,
  },
  actionStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },

  infoBannerGreen: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '11px 12px',
    borderRadius: 13,
    background: '#ecfdf5',
    color: '#166534',
    fontSize: fs(9.5),
    fontWeight: 700,
  },
  infoBannerTeal: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '11px 12px',
    borderRadius: 13,
    background: '#ecfeff',
    color: '#0f766e',
    fontSize: fs(9.5),
    fontWeight: 700,
  },
  infoBannerBlue: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '11px 12px',
    borderRadius: 13,
    background: '#eff6ff',
    color: '#1d4ed8',
    fontSize: fs(9.5),
    fontWeight: 700,
  },
  infoBannerPurple: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '11px 12px',
    borderRadius: 13,
    background: '#f5f3ff',
    color: '#6d28d9',
    fontSize: fs(9.5),
    fontWeight: 700,
  },
  infoBannerPink: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '11px 12px',
    borderRadius: 13,
    background: '#fdf2f8',
    color: '#be185d',
    fontSize: fs(9.5),
    fontWeight: 700,
  },
  infoBannerOrange: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '11px 12px',
    borderRadius: 13,
    background: '#fff7ed',
    color: '#c2410c',
    fontSize: fs(9.5),
    fontWeight: 700,
  },

  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    marginBottom: 12,
  },
  label: {
    fontSize: fs(9),
    fontWeight: 700,
    color: '#334155',
  },
  inputField: {
    height: 42,
    borderRadius: 13,
    border: '1px solid rgba(15,23,42,0.10)',
    padding: '0 12px',
    fontSize: fs(10),
    outline: 'none',
    background: '#fff',
    width: '100%',
    minWidth: 0,
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: 10,
  },
  formGridMobile: {
    gridTemplateColumns: '1fr',
  },
  miniInfoCard: {
    padding: 12,
    borderRadius: 14,
    background: '#fff',
    border: '1px solid rgba(15,23,42,0.07)',
  },
  miniInfoLabel: {
    display: 'block',
    fontSize: fs(8),
    color: '#64748b',
    marginBottom: 5,
  },
  miniInfoValue: {
    fontSize: fs(10.5),
    fontWeight: 800,
    color: '#0f172a',
    wordBreak: 'break-word',
  },

  primaryButton: {
    minHeight: 42,
    borderRadius: 13,
    border: 'none',
    background: 'linear-gradient(135deg, #16a34a, #166534)',
    color: '#fff',
    fontWeight: 800,
    fontSize: fs(10),
    cursor: 'pointer',
    boxShadow: '0 12px 26px rgba(22,163,74,0.22)',
    padding: '10px 14px',
    width: '100%',
  },
  secondaryButton: {
    minHeight: 40,
    padding: '9px 14px',
    borderRadius: 13,
    border: '1px solid rgba(15,23,42,0.08)',
    background: '#fff',
    color: '#0f172a',
    fontWeight: 700,
    fontSize: fs(10),
    cursor: 'pointer',
    width: '100%',
  },

  filterWrap: {
    display: 'flex',
    gap: 8,
    flexWrap: 'wrap',
  },
  filterButton: {
    padding: '7px 10px',
    borderRadius: 999,
    border: '1px solid rgba(15,23,42,0.08)',
    background: '#fff',
    cursor: 'pointer',
    fontSize: fs(8.5),
    fontWeight: 700,
    color: '#475569',
  },
  filterButtonActive: {
    background: '#0f172a',
    color: '#fff',
    border: '1px solid #0f172a',
  },

  tableWrap: {
    overflowX: 'auto',
    borderRadius: 16,
    border: '1px solid rgba(15,23,42,0.06)',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    minWidth: 760,
    background: '#fff',
  },
  th: {
    textAlign: 'left',
    fontSize: fs(8.5),
    fontWeight: 800,
    color: '#64748b',
    padding: '12px 14px',
    background: '#f8fafc',
    borderBottom: '1px solid rgba(15,23,42,0.06)',
  },
  tr: {
    borderBottom: '1px solid rgba(15,23,42,0.05)',
  },
  td: {
    padding: '12px 14px',
    fontSize: fs(9.5),
    color: '#475569',
  },
  tdStrong: {
    padding: '12px 14px',
    fontSize: fs(9.5),
    color: '#0f172a',
    fontWeight: 700,
  },
  tdCredit: {
    padding: '12px 14px',
    fontSize: fs(9.5),
    color: '#16a34a',
    fontWeight: 700,
  },
  subText: {
    fontSize: fs(8),
    color: '#94a3b8',
    marginTop: 4,
  },
  statusPill: {
    display: 'inline-flex',
    padding: '5px 8px',
    borderRadius: 999,
    fontSize: fs(8),
    fontWeight: 800,
  },
  statusPillSuccess: {
    background: '#ecfdf5',
    color: '#166534',
  },
  statusPillWarning: {
    background: '#fff7ed',
    color: '#c2410c',
  },
  statusPillInfo: {
    background: '#eff6ff',
    color: '#1d4ed8',
  },
  tableButtons: {
    display: 'flex',
    gap: 8,
    flexWrap: 'wrap',
  },
  tableApproveButton: {
    border: 'none',
    background: '#16a34a',
    color: '#fff',
    borderRadius: 9,
    padding: '7px 9px',
    fontSize: fs(8.5),
    fontWeight: 700,
    cursor: 'pointer',
  },
  tableRejectButton: {
    border: '1px solid rgba(15,23,42,0.08)',
    background: '#fff',
    color: '#0f172a',
    borderRadius: 9,
    padding: '7px 9px',
    fontSize: fs(8.5),
    fontWeight: 700,
    cursor: 'pointer',
  },

  mobileList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  mobileCard: {
    padding: 14,
    borderRadius: 16,
    border: '1px solid rgba(15,23,42,0.06)',
    background: '#fff',
  },
  mobileCardTop: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 10,
    flexWrap: 'wrap',
    marginBottom: 10,
  },
  mobileCardTitle: {
    fontSize: fs(10.5),
    fontWeight: 800,
    color: '#0f172a',
  },
  mobileCardMeta: {
    fontSize: fs(8.5),
    color: '#64748b',
    marginTop: 4,
  },
  mobileKeyValue: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 10,
    padding: '8px 0',
    fontSize: fs(9),
    color: '#475569',
    borderBottom: '1px dashed rgba(15,23,42,0.08)',
    flexWrap: 'wrap',
  },

  announcementList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  announcementItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    padding: 12,
    borderRadius: 14,
    background: '#f8fafc',
    border: '1px solid rgba(15,23,42,0.05)',
  },
  announcementIcon: {
    width: 32,
    height: 32,
    borderRadius: 11,
    background: '#ecfdf5',
    color: '#166534',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  announcementIconPink: {
    width: 32,
    height: 32,
    borderRadius: 11,
    background: '#fdf2f8',
    color: '#be185d',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  announcementIconWarning: {
    background: '#fff7ed',
    color: '#c2410c',
  },
  announcementIconBlue: {
    background: '#eff6ff',
    color: '#1d4ed8',
  },
  announcementTitle: {
    fontSize: fs(10),
    fontWeight: 800,
    color: '#0f172a',
    marginBottom: 4,
  },
  announcementText: {
    fontSize: fs(8.8),
    color: '#64748b',
    lineHeight: 1.6,
  },
  activityTime: {
    fontSize: fs(7.5),
    color: '#94a3b8',
    marginTop: 6,
  },

  goalAmount: {
    fontSize: fs(22),
    fontWeight: 900,
    color: '#0f172a',
    marginBottom: 7,
    wordBreak: 'break-word',
  },
  goalLabel: {
    color: '#64748b',
    fontSize: fs(9.5),
    marginBottom: 14,
  },
  progressTrack: {
    width: '100%',
    height: 9,
    borderRadius: 999,
    background: '#e2e8f0',
    overflow: 'hidden',
    marginBottom: 12,
  },
  progressFillBlue: {
    height: '100%',
    borderRadius: 999,
    background: 'linear-gradient(90deg, #2563eb, #60a5fa)',
  },
  progressFillTeal: {
    height: '100%',
    borderRadius: 999,
    background: 'linear-gradient(90deg, #0f766e, #14b8a6)',
  },
  progressMeta: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 10,
    flexWrap: 'wrap',
    fontSize: fs(8.5),
    color: '#64748b',
  },

  metricStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  metricChip: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    padding: 12,
    borderRadius: 14,
    background: '#f8fafc',
    border: '1px solid rgba(15,23,42,0.05)',
    fontSize: fs(9.5),
    color: '#475569',
    flexWrap: 'wrap',
  },

  financeGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: 10,
  },
  financeGridMobile: {
    gridTemplateColumns: '1fr',
  },
  financeCard: {
    padding: 14,
    borderRadius: 16,
    border: '1px solid rgba(15,23,42,0.07)',
    background: 'linear-gradient(180deg, #ffffff, #f8fafc)',
  },
  financeCardIcon: {
    width: 34,
    height: 34,
    borderRadius: 11,
    background: '#ecfdf5',
    color: '#166534',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 10,
  },
  financeCardTitle: {
    fontSize: fs(9),
    fontWeight: 700,
    color: '#64748b',
    marginBottom: 6,
  },
  financeCardValue: {
    fontSize: fs(13),
    fontWeight: 800,
    color: '#0f172a',
    wordBreak: 'break-word',
  },

  productsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gap: 12,
  },
  productsGridMobile: {
    gridTemplateColumns: '1fr',
  },
  productCard: {
    padding: 16,
    borderRadius: 18,
    border: '1px solid rgba(15,23,42,0.07)',
    background: 'linear-gradient(180deg, #ffffff, #f8fafc)',
  },
  productCardTop: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    marginBottom: 12,
  },
  productBadge: {
    display: 'inline-flex',
    padding: '5px 8px',
    borderRadius: 999,
    background: '#dcfce7',
    color: '#166534',
    fontSize: fs(8),
    fontWeight: 800,
  },
  productTitle: {
    fontSize: fs(11),
    fontWeight: 800,
    color: '#0f172a',
    marginBottom: 8,
  },
  productMeta: {
    fontSize: fs(9),
    color: '#16a34a',
    fontWeight: 700,
    marginBottom: 6,
  },
  productText: {
    fontSize: fs(8.8),
    color: '#64748b',
  },

  noteList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  noteItem: {
    padding: 12,
    borderRadius: 14,
    background: '#f8fafc',
    border: '1px solid rgba(15,23,42,0.05)',
    fontSize: fs(9.5),
    color: '#475569',
    lineHeight: 1.7,
  },

  emptyState: {
    textAlign: 'center',
    padding: 24,
    color: '#64748b',
    fontSize: fs(9.5),
  },
};

export default AdminDashboard;